<template lang="pug">
.page.page-contact
  .container
    .contact-hero
      p.contact-eyebrow {{ ui.eyebrow }}
      h1 {{ $t('contact.hero') }}
      p.hero-sub {{ $t('contact.heroSub') }}

    .contact-cta
      h2 {{ $t('contact.cta.title') }}
      p {{ $t('contact.cta.desc') }}
      a.email-btn(:href="contactHref()")
        span cheyu.wu@monoame.com
      button.copy-email(type="button", @click="copyEmail") {{ ui.copy }}
      span.copy-status(role="status", aria-live="polite") {{ copyStatus }}


    figure.contact-image
      SelectedPhoto(id="piano-portrait", eager, sizes="90vw")
      figcaption {{ $i18n.locale === 'zh' ? '演奏中的吳哲宇' : 'Che-Yu Wu at the piano' }}

    section.work-context(v-if="workContext", aria-labelledby="contact-work-title")
      h2#contact-work-title {{ ui.work }}
      p {{ workContext.title }}
      a(:href="workContext.url") {{ workContext.url }}
      button(type="button", @click="clearWork") {{ ui.clearWork }}



    .contact-intro
      p {{ $t('contact.intro1') }}
      p {{ $t('contact.intro2') }}
      p.highlight {{ $t('contact.intro3') }}

    .contact-grid
      a.contact-card(v-for="(kind, index) in contactKinds", :key="kind", :href="contactHref(kind)")
        span.contact-number {{ String(index + 1).padStart(2, '0') }}
        .contact-description
          h2.card-title {{ $t('contact.cards.' + kind + '.title') }}
          p.card-desc {{ $t('contact.cards.' + kind + '.desc') }}
        span.contact-arrow(aria-hidden="true") ↗︎


    .contact-links
      h3 {{ $t('contact.links.title') }}
      .links-grid
        a.link-item(href="https://www.instagram.com/cheyuwu345/", target="_blank", rel="noopener noreferrer")
          .link-label Instagram
          .link-handle @cheyuwu345
        a.link-item(href="https://open.spotify.com/artist/0AeZiCXhHzvexu47FOY5Tq", target="_blank", rel="noopener noreferrer")
          .link-label Spotify
          .link-handle {{ $t('contact.links.spotifyHandle') }}
        a.link-item(href="https://github.com/frank890417", target="_blank", rel="noopener noreferrer")
          .link-label GitHub
          .link-handle frank890417
        a.link-item(href="https://www.linkedin.com/in/cheyuwu/", target="_blank", rel="noopener noreferrer")
          .link-label LinkedIn
          .link-handle Che-Yu Wu
        a.link-item(href="https://streetvoice.com/frank890417/", target="_blank", rel="noopener noreferrer")
          .link-label StreetVoice
          .link-handle {{ $t('contact.links.streetvoiceHandle') }}
        a.link-item(href="https://muse.cheyuwu.com", target="_blank", rel="noopener noreferrer")
          .link-label Muse
          .link-handle {{ $t('contact.links.museHandle') }}

    section.artist-resources
      h2 {{ ui.resources }}
      nav(:aria-label="ui.resources")
        router-link(:to="local('/about/')") {{ ui.about }}
        router-link(:to="local('/cv/')") CV
        router-link(:to="local('/press/')") {{ ui.press }}

    .contact-closing
      p {{ $t('contact.closing') }}
</template>

<script>
import { withSlash } from '@/runtime/site-path.mjs'
import SelectedPhoto from '@/components/photos/SelectedPhoto.vue'
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'

const messages = {
  zh: { eyebrow: '吳哲宇 · 合作與聯絡', copy: '複製電子郵件', copied: '已複製電子郵件地址。', copyFailed: '無法自動複製，請選取上方地址。', imageAlt: '吳哲宇在鋼琴前演出 The Last Input', credit: '攝影：林亞蓁，C-LAB 委託拍攝', work: '關於這件作品', clearWork: '清除作品選擇', resources: '藝術家資料', about: '藝術家簡介', press: '報導與訪談', workPrefix: '作品：' },
  en: { eyebrow: 'CHE-YU WU · INQUIRIES', copy: 'Copy email', copied: 'Email address copied.', copyFailed: 'Copy unavailable. Please select the address above.', imageAlt: 'Che-Yu Wu performing The Last Input at the piano', credit: 'Photography: 林亞蓁, commissioned by C-LAB', work: 'About this work', clearWork: 'Clear work selection', resources: 'Artist resources', about: 'About the artist', press: 'Press & interviews', workPrefix: 'Work: ' }
}

export default {
  name: 'PageContact',
  components: { SelectedPhoto },
  data() { return { copyStatus: '' } },
  methods: {
    async copyEmail() {
      try {
        await navigator.clipboard.writeText('cheyu.wu@monoame.com')
        this.copyStatus = this.ui.copied
      } catch {
        this.copyStatus = this.ui.copyFailed
      }
    }
  },
  setup() {
    const { t } = useI18n()
    const contactKinds = ['exhibitions', 'teaching', 'brand', 'ai', 'chat']
    const route = useRoute(), router = useRouter()
    const zh = computed(() => route.path.startsWith('/zh/'))
    const ui = computed(() => messages[zh.value ? 'zh' : 'en'])
    const local = path => withSlash((zh.value ? '/zh' : '') + path)
    const workContext = computed(() => {
      const title = route.query.work, from = route.query.from
      if (typeof title !== 'string' || !title.trim() || title.length > 500 || typeof from !== 'string') return null
      // Only local artwork record paths are accepted; no protocol, host or query.
      if (!/^\/(?:zh\/)?(?:work|project|sketch)\/[^/?#\\]+\/?$/.test(from)) return null
      try {
        const decoded = decodeURIComponent(from)
        if (/[\\?#]/.test(decoded) || decoded.includes('..') || decoded.includes('//')) return null
        const url = new URL(from, 'https://cheyuwu.com')
        if (url.origin !== 'https://cheyuwu.com') return null
        return { title: title.replace(/[\r\n]/g, ' ').trim(), url: url.origin + url.pathname }
      } catch { return null }
    })
    const clearWork = () => {
      const { work, from, ...query } = route.query
      router.replace({ path: route.path, query, hash: route.hash })
    }
    const contactHref = kind => {
      const subject = kind ? t('contact.cards.' + kind + '.title') : ''
      const work = workContext.value
      if (!work) return 'mailto:cheyu.wu@monoame.com' + (subject ? '?subject=' + encodeURIComponent(subject) : '')
      const body = ui.value.workPrefix + work.title + '\n' + work.url
      return 'mailto:cheyu.wu@monoame.com?subject=' + encodeURIComponent([subject, work.title].filter(Boolean).join(' · ')) + '&body=' + encodeURIComponent(body)
    }
    return { t, ui, contactKinds, contactHref, zh, local, workContext, clearWork }
  }
}
</script>



<style scoped>
.page-contact {
  margin: 0 auto; text-align: left; max-width: min(90vw, 1640px); padding: 72px 0 96px; color: #f2eee5;
}
.contact-hero {
  text-align: left;
  margin-bottom: 32px; grid-column: 1; margin: 0; align-self: end;
}
.contact-hero h1 {
  color: #fff;
  margin-bottom: 16px; font-family: var(--font-display); font-size: clamp(36px,4vw,66px); font-weight: 400; line-height: 1.3; letter-spacing: -.02em;
}
.contact-hero .hero-sub {
  margin: 0;
  line-height: 1.8; color: #b8b5ad; font-size: 17px; max-width: 36ch;
}
.contact-intro {
  margin-bottom: 48px; text-align: left; margin: 112px 0 0; max-width: 780px;
}
.contact-intro p {
  line-height: 1.8;
  margin-bottom: 8px; color: #b8b5ad; font-size: 18px;
}
.contact-intro .highlight {
  color: #a088cc;
  font-style: italic;
  margin-top: 12px;
}
.contact-grid {
  display: block;
  border-top: 1px solid rgba(255,255,255,0.18); margin: 48px 0 112px;
}
.contact-card {
  display: grid;
  gap: 24px;
  text-align: left;
  border-bottom: 1px solid rgba(255,255,255,0.18);
  color: var(--ink);
  text-decoration: none; grid-template-columns: 64px minmax(0,1fr) 24px; padding: 32px 0;
}
.contact-card:hover {
  color: var(--gold);
  text-decoration: none;
}
.contact-card .contact-number {
  color: var(--ink-3);
  font-size: 0.8rem;
  padding-top: 5px;
}
.contact-card .card-title {
  font-family: var(--font-display);
  font-weight: 400;
  margin-bottom: 10px; font-size: 26px;
}
.contact-card .card-desc {
  font-size: 1rem;
  color: var(--ink-2);
  line-height: 1.8;
  margin: 0; max-width: 60ch;
}
@media (max-width: 640px) {
  .contact-card {
    grid-template-columns: 24px 1fr 18px;
    gap: 12px;
  }
}
.contact-cta {
  text-align: left;
  border-top: 1px solid rgba(255,255,255,0.06);
  margin-bottom: 48px; grid-column: 1; margin: 32px 0 0; padding: 28px 0 0; border-bottom: 0;
}
.contact-cta h2 {
  color: #fff;
  margin-bottom: 12px; font-size: 18px; font-weight: 400;
}
.contact-cta p {
  font-size: 1rem;
  margin-bottom: 20px;
  max-width: 500px;
  margin-left: 0;
  margin-right: 0;
  line-height: 1.7; color: #b8b5ad;
}
.email-btn {
  display: inline-block;
  border-bottom: 1px solid rgba(255,255,255,0.4);
  overflow-wrap: anywhere;
  color: #fff;
  text-decoration: none;
  letter-spacing: 0.01em;
  transition: var(--t-hover); font-family: var(--font-slab); font-size: clamp(22px,2.5vw,38px); padding: 12px 0; line-height: 1.2;
}
.email-btn:hover {
  border-color: #fff; color: var(--gold); background: transparent;
}
.contact-links {
  margin-bottom: 48px; text-align: left; margin: 0;
}
.contact-links h3 {
  font-size: 1.1rem;
  font-weight: 300;
  color: #888;
  margin-bottom: 24px;
}
.links-grid {
  grid-template-columns: repeat(3, 1fr);
  gap: 12px; display: block; margin: 0; max-width: none;
}
@media (max-width: 768px) {
  .links-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
.link-item {
  flex-direction: column;
  align-items: center;
  text-decoration: none;
  color: #ccc;
  transition: var(--t-hover); display: grid; grid-template-columns: 1fr 1fr; text-align: left; padding: 16px 0; border: 0; border-bottom: 1px solid #34322e; border-radius: 0; gap: 12px;
}
.link-item:hover {
  border-color: rgba(255,255,255,0.2);
  background: rgba(255,255,255,0.02);
  color: #fff;
  text-decoration: none;
}
.link-item .link-label {
  font-weight: 500;
  margin-bottom: 2px; font-size: 16px; margin: 0;
}
.link-item .link-handle {
  font-size: 0.8rem; color: #b8b5ad; text-align: right; overflow-wrap: anywhere;
}
.work-context,
.artist-resources {
  margin: 32px 0 48px;
  padding: 24px 0;
  border-top: 1px solid #555;
  overflow-wrap: anywhere;
}
.work-context h2,
.artist-resources h2 {
  font-size: 1.2rem;
}
.work-context a,
.artist-resources a,
.work-context button,
.artist-resources button {
  color: inherit;
}
.work-context button,
.artist-resources button {
  display: block;
  margin-top: 16px;
  min-height: 40px;
  padding: 8px 18px;
  background: var(--tint-2);
  border: 0;
  border-radius: var(--r-pill);
  cursor: pointer;
  transition: var(--t-hover);
}
.work-context button:hover,
.artist-resources button:hover {
  background: var(--tint-3);
}
.work-context nav,
.artist-resources nav {
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
}
.contact-closing {
  padding-top: 16px; margin-top: 96px; text-align: left;
}
.contact-closing p {
  font-size: 0.9rem;
  font-style: italic; color: #b8b5ad;
}
.page-contact > .container { max-width: none; padding: 0; display: grid; grid-template-columns: minmax(0,1fr) minmax(0,1fr); column-gap: 7%; }
.contact-eyebrow { font-size: 12px; letter-spacing: .13em; color: #b8b5ad; margin-bottom: 28px; }
.copy-email { display: block; border: 0; border-bottom: 1px solid #706b61; padding: 10px 0; margin-top: 12px; min-height: 44px; background: transparent; color: #d8d1c4; cursor: pointer; }
.copy-status { display: block; min-height: 24px; font-size: 13px; color: var(--gold); margin-top: 8px; }
.contact-image { grid-column: 2; grid-row: 1 / 3; margin: 0; align-self: stretch; }
.contact-image img { width: 100%; height: calc(100% - 45px); max-height: 720px; min-height: 400px; object-fit: cover; object-position: 48% 48%; }
.contact-image figcaption { font-size: 11px; color: #b8b5ad; line-height: 1.6; margin-top: 10px; }
.work-context,.contact-intro,.contact-grid,.contact-closing { grid-column: 1 / -1; }
.contact-description { display: grid; grid-template-columns: minmax(180px, .8fr) minmax(0,1.2fr); gap: 40px; }
.contact-arrow { transition: transform var(--dur-quick) var(--ease-out); }
.contact-card:hover .contact-arrow { transform: translate(3px,-3px); }
.contact-links h3,.artist-resources h2 { font-family: var(--font-display); color: #f2eee5; font-size: 26px; font-weight: 400; }
.artist-resources { margin: 0; padding: 0; border-top: 0; }
.artist-resources nav { align-items: stretch; text-align: left; display: flex; flex-direction: column; gap: 0; }
.artist-resources a { padding: 18px 0; min-height: 44px; border-bottom: 1px solid #34322e; text-underline-offset: 4px; }
.page-contact a:focus-visible,.page-contact button:focus-visible { outline: 2px solid var(--gold); outline-offset: 5px; }
@media(max-width: 767px) {
 .page-contact { padding-top: 40px; }
 .page-contact > .container { display: flex; flex-direction: column; }
 .contact-hero h1 { font-size: 36px; }
 .contact-cta { margin-top: 24px; }
 .contact-image { margin-top: 36px; }
 .contact-image img { height: auto; min-height: 0; max-height: none; object-fit: contain; }
 .contact-intro { margin-top: 64px; }
 .contact-grid { margin-bottom: 64px; }
 .contact-card { grid-template-columns: 24px minmax(0,1fr) 18px; gap: 12px; }
 .contact-description { display: block; }
 .contact-card .card-title { font-size: 24px; }
 .artist-resources { margin-top: 56px; }
 .contact-closing { margin-top: 64px; }
}
@media(prefers-reduced-motion: reduce) { .page-contact a,.contact-arrow { transition: none; } .contact-card:hover .contact-arrow { transform: none; } }

</style>
